/**
 * SalonID CTA Marquee - Enhanced Version
 * RAF-based vertical marquee with center fade effect
 */
(function() {
  'use strict';

  class CTAMarqueeAnimator {
    constructor(section) {
      this.section = section;
      this.container = section.querySelector('.salonid-cta-marquee__container');
      this.marqueeContainer = section.querySelector('[data-marquee-container]');
      this.marqueeTracks = section.querySelectorAll('[data-marquee-track]');
      this.marqueeItems = [];
      
      this.scrollPosition = 0;
      this.speed = 30; // pixels per second
      this.isVisible = false;
      this.animationFrame = null;
      this.lastTimestamp = 0;
      
      this.init();
    }

    init() {
      // Setup reversible container animation
      this.setupIntersectionObserver();
      
      if (!this.marqueeContainer || this.marqueeTracks.length === 0) return;
      
      // Collect all marquee items
      this.collectMarqueeItems();
      
      // Start marquee animation
      this.startMarqueeAnimation();
      
      // Setup touch pause
      this.setupTouchEvents();
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: show immediately
        if (this.container) {
          this.container.classList.add('animate-in');
        }
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          
          if (entry.isIntersecting) {
            this.container.classList.remove('animate-out');
            this.container.classList.add('animate-in');
            
            // Resume marquee if paused
            if (this.animationFrame === null) {
              this.startMarqueeAnimation();
            }
          } else {
            if (this.container.classList.contains('animate-in')) {
              this.container.classList.remove('animate-in');
              this.container.classList.add('animate-out');
            }
            
            // Pause marquee when not visible
            if (this.animationFrame !== null) {
              cancelAnimationFrame(this.animationFrame);
              this.animationFrame = null;
            }
          }
        });
      }, {
        threshold: [0, 0.2, 0.5, 1],
        rootMargin: '-50px 0px'
      });

      observer.observe(this.section);
      this.observer = observer;
    }

    collectMarqueeItems() {
      this.marqueeTracks.forEach(track => {
        const items = Array.from(track.querySelectorAll('.salonid-cta-marquee__marquee-item'));
        this.marqueeItems.push(...items);
      });
    }

    setupTouchEvents() {
      let isPaused = false;

      this.marqueeContainer.addEventListener('touchstart', () => {
        isPaused = true;
      }, { passive: true });

      this.marqueeContainer.addEventListener('touchend', () => {
        isPaused = false;
      }, { passive: true });

      this.marqueeContainer.addEventListener('mouseenter', () => {
        isPaused = true;
      });

      this.marqueeContainer.addEventListener('mouseleave', () => {
        isPaused = false;
      });

      this.isPausedCallback = () => isPaused;
    }

    startMarqueeAnimation() {
      this.lastTimestamp = performance.now();

      const animate = (currentTimestamp) => {
        // Calculate delta time in seconds
        const deltaTime = (currentTimestamp - this.lastTimestamp) / 1000;
        this.lastTimestamp = currentTimestamp;

        // Check if paused
        const isPaused = this.isPausedCallback ? this.isPausedCallback() : false;

        if (!isPaused && this.isVisible) {
          // Update scroll position
          this.scrollPosition += this.speed * deltaTime;

          // Calculate track height for seamless loop
          if (this.marqueeTracks.length > 0) {
            const trackHeight = this.marqueeTracks[0].offsetHeight;
            
            // Reset when scrolled past one complete track
            if (this.scrollPosition >= trackHeight) {
              this.scrollPosition -= trackHeight;
            }

            // Apply transform to both tracks
            this.marqueeTracks.forEach(track => {
              track.style.transform = `translate3d(0, -${this.scrollPosition}px, 0)`;
            });
          }

          // Update opacity based on position
          this.updateItemOpacity();
        }

        this.animationFrame = requestAnimationFrame(animate);
      };

      this.animationFrame = requestAnimationFrame(animate);
    }

    updateItemOpacity() {
      if (!this.marqueeContainer) return;

      const containerRect = this.marqueeContainer.getBoundingClientRect();
      const centerY = containerRect.top + containerRect.height / 2;

      this.marqueeItems.forEach(item => {
        const itemRect = item.getBoundingClientRect();
        const itemCenterY = itemRect.top + itemRect.height / 2;

        // Calculate distance from center
        const distance = Math.abs(centerY - itemCenterY);
        const maxDistance = containerRect.height / 2;

        // Normalize distance (0 at center, 1 at edge)
        const normalizedDistance = Math.min(distance / maxDistance, 1);

        // Calculate opacity (1 at center, 0.2 at edge)
        // Smoother falloff curve
        const opacity = 1 - Math.pow(normalizedDistance, 1.5) * 0.8;

        // Round to 2 decimals to reduce updates
        const roundedOpacity = Math.round(opacity * 100) / 100;
        
        // Only update if changed significantly
        const currentOpacity = parseFloat(item.style.opacity || 1);
        if (Math.abs(currentOpacity - roundedOpacity) > 0.01) {
          item.style.opacity = roundedOpacity.toString();
        }
      });
    }

    destroy() {
      // Clean up animation frame
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      // Clean up observer
      if (this.observer) {
        this.observer.disconnect();
      }

      // Clean up will-change
      this.marqueeTracks.forEach(track => {
        track.style.willChange = 'auto';
      });
    }
  }

  let animators = [];

  function initCTAMarquee() {
    // Check for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      // Show content but no animation
      document.querySelectorAll('.salonid-cta-marquee__container').forEach(container => {
        container.classList.add('animate-in');
      });
      return;
    }

    // Clean up existing animators
    animators.forEach(animator => animator.destroy());
    animators = [];

    const sections = document.querySelectorAll('.salonid-cta-marquee');
    
    if (sections.length === 0) {
      return;
    }

    sections.forEach(section => {
      const animator = new CTAMarqueeAnimator(section);
      animators.push(animator);
    });
    
    console.log('✅ SalonID CTA Marquee initialized -', animators.length, 'sections');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCTAMarquee);
  } else {
    initCTAMarquee();
  }

  // Re-initialize on Shopify theme editor updates
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(initCTAMarquee, 100);
    });
    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(initCTAMarquee, 100);
    });
  }

  // Cleanup on unload
  window.addEventListener('beforeunload', () => {
    animators.forEach(animator => animator.destroy());
  });

  // Export for potential external use
  window.SalonIDCTAMarquee = CTAMarqueeAnimator;
})();
